/**
 * Enum values shared by the Drizzle schema, the API contract, and the UI.
 *
 * These are the single source of truth: `server/src/db/schema.ts` builds its
 * pgEnums from these arrays, so adding a value here and regenerating a
 * migration is the only step needed to add one everywhere.
 */

export const GAME_TYPES = ['game', 'dlc', 'bundle', 'software'] as const
export type GameType = (typeof GAME_TYPES)[number]

/**
 * `reserved` is the transient state while a request is being approved but the
 * code has not yet been handed over. `revoked` covers keys that turned out to
 * be dead — already redeemed, region-locked, pulled by the seller.
 */
export const KEY_STATUSES = ['available', 'reserved', 'claimed', 'expired', 'revoked'] as const
export type KeyStatus = (typeof KEY_STATUSES)[number]

export const KEY_EVENT_TYPES = [
  'created',
  'reserved',
  'released',
  'claimed',
  'expired',
  'revoked',
] as const
export type KeyEventType = (typeof KEY_EVENT_TYPES)[number]

/**
 * What a key unlocks. Structured rather than free text so it can be shown
 * consistently and filtered on; the optional `edition` description carries
 * the specifics ("Digital Deluxe Edition", "The Eldritch Box").
 */
export const UNLOCK_TYPES = ['base', 'bundle', 'dlc', 'other'] as const
export type UnlockType = (typeof UNLOCK_TYPES)[number]

export const UNLOCK_TYPE_LABELS: Record<UnlockType, string> = {
  base: 'Base game',
  bundle: 'Bundle',
  dlc: 'DLC',
  other: 'Other',
}

/** Longer hints for the admin dropdown. */
export const UNLOCK_TYPE_HINTS: Record<UnlockType, string> = {
  base: 'The game itself',
  bundle: 'Deluxe, Ultimate, Complete editions and the like',
  dlc: 'Add-on content for a game',
  other: 'Anything else — a skin, a soundtrack, a multiplayer key',
}

export const REQUEST_STATUSES = ['pending', 'approved', 'declined', 'cancelled'] as const
export type RequestStatus = (typeof REQUEST_STATUSES)[number]

/** A key is only offerable to the public in this state. */
export const AVAILABLE_STATUS: KeyStatus = 'available'

export const GAME_TYPE_LABELS: Record<GameType, string> = {
  game: 'Game',
  dlc: 'DLC',
  bundle: 'Bundle',
  software: 'Software',
}

export const KEY_STATUS_LABELS: Record<KeyStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  claimed: 'Claimed',
  expired: 'Expired',
  revoked: 'Revoked',
}

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
}
